import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { ImageSocialPanel, imageDetailQueryKey } from '../components/community/ImageSocialPanel';
import { formatEntryDate } from '../components/community/CommunityFeedItem';
import { getImageDetail } from '../services/communityService';
import { useTranslation } from '../i18n/useTranslation';
import './SharedImagePage.css';

/**
 * /images/:imageId — the target of an image share link
 * (GET /api/v1/images/:imageId/share). Sign-in required: the API serves images
 * only to signed-in users (public ones to everyone signed in, private ones to
 * the owner).
 */
export function SharedImagePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { imageId = '' } = useParams<{ imageId: string }>();

  const { data: image, isLoading, isError, refetch } = useQuery({
    queryKey: imageDetailQueryKey(imageId),
    queryFn: () => getImageDetail(imageId),
    enabled: Boolean(imageId),
    retry: false,
  });

  const back = (
    <Button variant="ghost" onClick={() => navigate('/community')}>
      {t('sharedImage.back')}
    </Button>
  );

  let body;
  if (isLoading) {
    body = (
      <PageHeader title={t('sharedImage.title')} back={back}>
        <LiveRegion data-testid="shared-image-loading">
          <Text color="muted">{t('sharedImage.loading')}</Text>
        </LiveRegion>
      </PageHeader>
    );
  } else if (isError || !image) {
    body = (
      <>
        <PageHeader title={t('sharedImage.title')} back={back} />
        <LoadErrorState
          data-testid="shared-image-error"
          title={t('sharedImage.loadError')}
          onRetry={() => void refetch()}
        />
      </>
    );
  } else {
    const aquarium = image.aquariumName || t('community.unnamedAquarium');
    body = (
      <>
        <PageHeader
          title={t('sharedImage.title')}
          description={`${t('community.aquarium', { name: aquarium })} · ${formatEntryDate(image.photoTakenAt, i18n.language)}`}
          back={back}
        />
        <Card padding="none" className="shared-image-page__media">
          {image.displayUrl ? (
            <img
              className="shared-image-page__img"
              src={image.displayUrl}
              alt={t('sharedImage.alt', { aquarium })}
            />
          ) : (
            <Text color="muted" className="shared-image-page__missing">{t('sharedImage.noFile')}</Text>
          )}
        </Card>
        <Section title={t('imageSocial.title')}>
          <Card>
            <ImageSocialPanel imageId={image.id} headingLevel={3} />
          </Card>
        </Section>
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" data-testid="shared-image-page">
          {body}
        </PageContent>
      }
    />
  );
}

export default SharedImagePage;
